import React, { useEffect, useRef, useState } from 'react';
import { EMPTY_STRING_REGEXP, getErrorMessage } from 'Utils';
import { Keyboard, TextInput, ScrollView, Text, View } from 'react-native';
import { t } from 'Locale';
import { withStore, useStore } from 'Store';
import { useAppEntryContext } from 'Pages/AppEntry';
import Theme from 'Theme';
import {
    ActivityIndicator,
    Button,
    Checkbox,
    Hr,
    InfoBox,
    Link,
    ModalHeader,
    InputWithIconButton,
    CustomKeyboardAvoidingView,
    Icon
} from 'LocalGluons';
import { Device } from 'DeviceUtils';
import storeActions from 'Store/storeActions';
import { useAppDispatch } from 'ReduxStore/hooks';
import { setActiveMenuItem } from 'ReduxStore/webMenu';
import { logEvent } from 'AppManager';
import { CommonActions, useNavigation } from '@react-navigation/native';
import { HIDE_REGISTRATION_BUTTON } from '../../../options';
import styles from './Login.jss';
import { useNavigationPropsContext } from '../../Navigation/navigationPropsContext';

const LoginPage = ({ setHideLoginHeader }) => {
    const { appManager, userData, deviceStorageState } = useStore();
    const { initialize } = useAppEntryContext();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loginErrorMessage, setLoginErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [hasErrorMessage, setHasErrorMessage] = useState(false);
    const { navigate } = useNavigationPropsContext();
    const dispatch = useAppDispatch();
    const usernameRef = useRef<TextInput>(null);
    const passwordRef = useRef<TextInput>(null);
    const [loginWasPressed, setLoginWasPressed] = useState(false);
    const navigation = useNavigation();

    useEffect(() => {
        if (userData) {
            navigation.dispatch(
                CommonActions.reset({
                    index: 0,
                    routes: [
                        {
                            name: 'Main', state: {
                                routes: [{ name: 'home' }],
                            }
                        },
                    ],
                })
            );
        }
    }, [userData]);

    const validate = () => {
        let valid = true;

        if (!username || EMPTY_STRING_REGEXP.test(username)) {
            setLoginErrorMessage(t('ERROR.INVALID_CREDENTIALS'));
            setHasErrorMessage(true);
            valid = false;
        } else if (!password || EMPTY_STRING_REGEXP.test(password)) {
            setLoginErrorMessage(t('ERROR.INVALID_CREDENTIALS'));
            setHasErrorMessage(true);
            valid = false;
        }

        return valid;
    };

    const onLoginPressed = async () => {
        await appManager.wipeCache();
        const isValid = validate();
        Keyboard.dismiss();
        setLoginWasPressed(true);
        if (isValid) {
            setLoading(true);
            setLoginErrorMessage('');
            try {
                await appManager.login(username, password);
                dispatch(setActiveMenuItem('home'));
                await initialize(true);
            } catch (error) {
                setLoading(false);
                setHasErrorMessage(true);
                setLoginErrorMessage(getErrorMessage(error));
            }
        }
    };

    const onForgottenPassword = () => {
        navigate('PasswordManagement', { initialRoute: 'sendCode' });
    };

    const onUserNameSubmit = () => {
        if (usernameRef.current) {
            usernameRef.current.focus();
        }
        onLoginPressed();
    };

    const onLearnMore = () => {
        navigate('InfoScene', {});
    };
    const onForgottenUsername = () => {
        // const { navigation, setHideLoginHeader } = props;
        setHideLoginHeader(true);
        navigation.navigate('ForgottenUsername');
        logEvent({
            type: 'user-flow',
            subtype: 'login-page',
            content1: 'forgotten-username',
            content2: '',
            context: '/forgotten-username'
        });
    };

    // const onSuspendError = () => { //unsed 
    //     navigate('SuspendScene', {});
    //     setLoginErrorMessage('');
    // };

    const onClose = () => {
        if (navigation.canGoBack()) {
            navigation.popToTop();
        } else {
            navigation.navigate('Main', { screen: 'watch-tv' });
        }
    };

    // const onRememberPasswordClicked = (checked: boolean) => {//removed functionality during username help redisgn
    //     deviceStorageDispatch({ type: storeActions.setRememberPassword, payload: checked });
    // };

    useEffect(() => () => setLoginWasPressed(false), []);

    const trialButtonShow = t('COMMON.START_FREE_TRIAL_BUTTON_MOBILES').trim() ? true : false;

    return (
        <CustomKeyboardAvoidingView keyboardVerticalOffset={0}>
            <View style={Device.isTabletOrWeb ? styles.tabletWebContainer : styles.mobileContainer}>
                <ScrollView
                    contentContainerStyle={styles.container}
                    keyboardShouldPersistTaps="handled"
                >
                    <ModalHeader
                        style={{ iconContainer: styles.modalHeaderIconContainer }}
                        iconName="close"
                        onPress={onClose}
                    />
                    <View style={[styles.innerContainer, hasErrorMessage && { marginTop: 0 }]}>
                        <View style={styles.titleContainer}>
                            <Text style={styles.title}>{t('COMMON.LOGIN')}</Text>
                        </View>
                        <Hr />

                        {hasErrorMessage && loginErrorMessage !== 'NO_ACTIVE_ACCOUNTS' && (
                            <InfoBox
                                isPositive={false}
                                message={loginErrorMessage}
                                style={styles.infoBox}
                            />
                        )}
                        {/*
                        {hasErrorMessage &&
                            loginErrorMessage === 'NO_ACTIVE_ACCOUNTS' &&
                            onSuspendError()} */}

                        {/*   <TextInput
                            ref={usernameRef}
                            testID="username_input"
                            style={[styles.input, styles.firstInput, styles.inputContainerItem]}
                            placeholder={t('COMMON.USERNAME')}
                            autoCapitalize="none"
                            placeholderTextColor={Theme.placeholderTextColor}
                            onChangeText={setUsername}
                            value={username}
                            returnKeyType="next"
                            onSubmitEditing={onUserNameSubmit}
                        /> */}
                        <View style={styles.inputContainer}>
                            <TextInput
                                ref={usernameRef}
                                testID="username_input"
                                style={[styles.input, styles.firstInput, styles.inputContainerItem]}
                                placeholder={t('COMMON.USERNAME')}
                                autoCapitalize="none"
                                placeholderTextColor={Theme.placeholderTextColor}
                                onChangeText={setUsername}
                                secureTextEntry={false}
                                value={username}
                                returnKeyType="next"
                                onSubmitEditing={onUserNameSubmit}
                            />

                            <View style={styles.switchContainer}>
                                <Icon
                                    name="infoCircleColor"
                                    style={styles.info}
                                    testID="input_button_icon"
                                    onPress={onForgottenUsername}
                                />
                            </View>
                        </View>
                        <InputWithIconButton
                            setRef={passwordRef}
                            testID="password_input"
                            style={styles.input}
                            placeholder={t('COMMON.PASSWORD')}
                            placeholderTextColor={Theme.placeholderTextColor}
                            onChangeText={setPassword}
                            value={password}
                            returnKeyType="done"
                            onSubmitEditing={onLoginPressed}//* *Login after enter clicked on web */
                            showIcon
                            loginWasPressed={loginWasPressed}
                        />
                        {/*  <View style={styles.rememberPassContainer}>
                            <Checkbox
                                checked={deviceStorageState.rememberPassword}
                                onPress={onRememberPasswordClicked}
                                hitSlop={{
                                    top: 0,
                                    bottom: 0,
                                    left: 0,
                                    right: getActualWidth()
                                }}
                            />
                            <Text
                                testID="rememberPassword"
                                style={styles.rememberPassText}
                                numberOfLines={2}
                            >
                                {t('LOGIN.REMEMBER_PASSWORD')}
                            </Text>
                        </View> */}
                        <Link
                            testID="forgotten_username"
                            style={
                                (!Device.isWeb && styles.forgottenUsernameText) || {
                                    fontSize: 21,
                                    marginTop: 10,
                                    marginBottom: 10
                                }
                            }
                            label={t('LOGIN.HELP.USERNAME_BTN_MOBILES')}
                            onPress={onForgottenUsername}
                        />
                        <Link
                            testID="forgotten_password"
                            style={(!Device.isWeb && styles.forgottenPassText) || { fontSize: 21 }}
                            label={t('LOGIN.FORGOTTEN_PASSWORD')}
                            onPress={onForgottenPassword}
                        />
                        <View
                            style={[
                                styles.buttonsContainer,
                                HIDE_REGISTRATION_BUTTON && styles.buttonContainerOneButton
                            ]}
                        >
                            <Button
                                testID="login_button"
                                style={{
                                    button: {
                                        ...styles.inputContainerItem,
                                        ...styles.button
                                    }
                                }}
                                title={t('COMMON.LOGIN')}
                                onPress={onLoginPressed}
                            />
                            {trialButtonShow && (
                                <Button
                                    testID="register_button"
                                    isSecondary
                                    style={{
                                        button: {
                                            ...styles.inputContainerItem,
                                            ...styles.button,
                                            ...styles.loginButton
                                        }
                                    }}
                                    title={t('COMMON.START_FREE_TRIAL_BUTTON_MOBILES')}
                                    onPress={onLearnMore}
                                />
                            )}
                        </View>
                    </View>
                </ScrollView>
                {loading && <ActivityIndicator isOverlay modal />}
            </View>
        </CustomKeyboardAvoidingView>
    );
};
export default withStore(LoginPage);
